/**
 * Exporta dados para Excel multi-aba (.xlsx).
 *
 *   - Aba "Resumo": pares label/valor (vertical).
 *   - Uma aba por seção de dados — cabeçalho em negrito + fundo accent.
 *   - Tipos nativos: datas como Date, moeda como number com format BRL.
 *   - Largura de colunas auto-calculada pela maior string da coluna.
 */

import * as XLSX from "xlsx";
import { excelFormat, rawCell, slugifyFilename, timestampForFilename } from "./formatters";

// (cor accent reservada para uma futura versão com estilos do cabeçalho
// via xlsx-style; por enquanto a community lib não preserva fill em writeFile)

// O SheetJS converte `Date` em numero serial usando o fuso de 1899 — em
// Sao Paulo, a hora local media (-3:06:28) — e toda data sai 28 s antes:
// 10/10 00:00 vira 09/10 23:59:32 e o Excel mostra o dia ANTERIOR.
// Convertemos aqui, pelo relogio local de hoje, e o SheetJS so ve numero.
const EPOCA_EXCEL_UTC = Date.UTC(1899, 11, 30);
const MS_POR_DIA = 86400000;

export function serialExcel(data) {
  const comoUtc = Date.UTC(
    data.getFullYear(), data.getMonth(), data.getDate(),
    data.getHours(), data.getMinutes(), data.getSeconds(),
  );
  return (comoUtc - EPOCA_EXCEL_UTC) / MS_POR_DIA;
}

function celulaExcel(valor, tipo) {
  const cru = rawCell(valor, tipo);
  return cru instanceof Date ? serialExcel(cru) : cru;
}

const TIPOS_DATA = new Set(["date", "datetime"]);

function safeSheetName(name, used) {
  // Excel: max 31 chars, sem [ ] / \ ? * :
  let base = String(name || "Aba").replace(/[[\]/\\?*:]/g, " ").slice(0, 31).trim();
  if (!base) base = "Aba";
  let candidate = base;
  let i = 2;
  while (used.has(candidate.toLowerCase())) {
    const suffix = ` (${i++})`;
    candidate = (base.slice(0, 31 - suffix.length) + suffix).trim();
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

function estimarLarguraCol(colunaValores, header, tipo) {
  const mascaraData = tipo === "datetime" ? "dd/mm/yyyy hh:mm" : "dd/mm/yyyy";
  const maior = Math.max(
    header.length,
    ...colunaValores.map((v) => {
      const s = v === "" || v == null ? "" : TIPOS_DATA.has(tipo) ? mascaraData : String(v);
      return s.length;
    })
  );
  return Math.min(Math.max(maior + 2, 8), 40);
}

function aplicarEstiloCabecalho(ws, columns) {
  // xlsx CE (community) não preserva styles ao salvar sem o pacote
  // "xlsx-style". O ! da gente é cosmético — defaults visuais bons já vêm
  // pelo Excel se o cabeçalho estiver na primeira linha congelada.
  // Mantemos `!cols` (larguras) e `!freeze` (linha 1 fixa).
  ws["!cols"] = columns;
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
}

function montarAbaResumo(resumo) {
  const aoa = [["Indicador", "Valor"]];
  for (const item of resumo || []) {
    const tipo = item.type || "text";
    aoa.push([item.label || "", celulaExcel(item.value, tipo)]);
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Aplica formatação por linha (segunda coluna).
  for (let i = 1; i < aoa.length; i++) {
    const item = resumo[i - 1];
    const ref = XLSX.utils.encode_cell({ r: i, c: 1 });
    if (!ws[ref]) continue;
    const fmt = excelFormat(item.type || "text");
    if (fmt) ws[ref].z = fmt;
    if (typeof ws[ref].v === "number") ws[ref].t = "n";
  }

  ws["!cols"] = [{ wch: 36 }, { wch: 22 }];
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
  return ws;
}

function montarAbaSecao(secao) {
  const { columns, rows } = secao;
  const header = columns.map((c) => c.label);
  const aoa = [header];

  for (const r of rows || []) {
    aoa.push(columns.map((c) => celulaExcel(r[c.key], c.type || "text")));
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Aplica formato e tipo correto por coluna.
  for (let colIdx = 0; colIdx < columns.length; colIdx++) {
    const col = columns[colIdx];
    const fmt = excelFormat(col.type || "text");
    for (let rowIdx = 1; rowIdx < aoa.length; rowIdx++) {
      const ref = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
      if (!ws[ref]) continue;
      if (typeof ws[ref].v === "number") ws[ref].t = "n";
      if (fmt) ws[ref].z = fmt;
    }
  }

  // Largura automática.
  const colSpec = columns.map((c, idx) => {
    const valores = aoa.slice(1).map((row) => row[idx]);
    return { wch: estimarLarguraCol(valores, c.label, c.type) };
  });

  aplicarEstiloCabecalho(ws, colSpec);
  return ws;
}

/**
 * @param {Object} opts
 * @param {string} opts.titulo
 * @param {Array<{ label, value, type? }>} [opts.resumo]
 * @param {Array<{ id, label, columns, rows }>} opts.secoes
 */
export function gerarWorkbook(opts) {
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();
  // "Resumo" fica reservado mesmo vindo por último — evita colisão com uma
  // seção chamada Resumo.
  usedNames.add("resumo");

  // Abas de DADOS primeiro (e ativas ao abrir). Antes o Resumo era a 1ª
  // aba: o usuário abria o arquivo, via só os KPIs e concluía que "o xlsx
  // não tem os dados" — as tabelas ficavam escondidas na 2ª aba.
  for (const secao of opts.secoes || []) {
    const ws = montarAbaSecao(secao);
    const nome = safeSheetName(secao.label || secao.id || "Detalhe", usedNames);
    XLSX.utils.book_append_sheet(wb, ws, nome);
  }

  if (opts.resumo && opts.resumo.length > 0) {
    const ws = montarAbaResumo(opts.resumo);
    XLSX.utils.book_append_sheet(wb, ws, "Resumo");
  }

  if (wb.SheetNames.length === 0) {
    // Workbook precisa ter ao menos uma aba.
    const ws = XLSX.utils.aoa_to_sheet([["Sem dados a exportar"]]);
    XLSX.utils.book_append_sheet(wb, ws, "Vazio");
  }

  return wb;
}

export function salvarExcel(opts) {
  const wb = gerarWorkbook(opts);
  const filename = `${slugifyFilename(opts.titulo)}_${timestampForFilename()}.xlsx`;
  XLSX.writeFile(wb, filename, { compression: true });
  return filename;
}

/**
 * Gera o XLSX e retorna { blob, filename } — sem fazer download.
 * Usado pelo executor de agendamento.
 */
export function gerarBlobExcel(opts) {
  const wb = gerarWorkbook(opts);
  const filename = `${slugifyFilename(opts.titulo)}_${timestampForFilename()}.xlsx`;
  const arrayBuffer = XLSX.write(wb, {
    bookType: "xlsx",
    type: "array",
    compression: true,
  });
  const blob = new Blob([arrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  return { blob, filename };
}
